import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Bell, BookOpen, CheckCheck, ChevronRight, CreditCard, FileText, GraduationCap, LifeBuoy, MessageSquare, Wallet } from 'lucide-react';
import { getNotifications, postNotificationsRead, postNotificationsReadAll } from '@/api/common';
import { metaOf, unwrap } from '@/components/kit/data';
import { useBase } from '@/lib/useBase';

interface Row { id: number; type: string; title: string; body: string; read: boolean; at: string; data?: Record<string, any> | null }

/** Icon, colour and the page to open for each kind of notification. */
function kind(type: string): { icon: any; tone: string; to?: string } {
  const f = type.split('.')[0];
  if (type === 'notice.critical') return { icon: AlertTriangle, tone: 'bg-red-50 text-red-600', to: 'notices' };
  switch (f) {
    case 'finance': return { icon: Wallet, tone: 'bg-amber-50 text-amber-700', to: 'approvals' };
    case 'payment': case 'fees': case 'wallet': return { icon: CreditCard, tone: 'bg-green-50 text-green-700' };
    case 'payroll': return { icon: Wallet, tone: 'bg-green-50 text-green-700', to: 'my-pay' };
    case 'results': case 'cbt': return { icon: GraduationCap, tone: 'bg-purple-50 text-purple-700', to: 'results' };
    case 'lesson': case 'lesson_note': case 'assignment': return { icon: BookOpen, tone: 'bg-blue-50 text-blue-700' };
    case 'message': return { icon: MessageSquare, tone: 'bg-sky-50 text-sky-700', to: 'messages' };
    case 'support': return { icon: LifeBuoy, tone: 'bg-slate-100 text-slate-600', to: 'support' };
    case 'notice': return { icon: Bell, tone: 'bg-[#0A2540]/5 text-[#0A2540]', to: 'notices' };
    case 'admission': case 'alumni': case 'student': return { icon: FileText, tone: 'bg-slate-100 text-slate-600' };
    default: return { icon: Bell, tone: 'bg-slate-100 text-slate-600' };
  }
}
/** Pages that exist in each portal, so a tap never lands on a missing page. */
const HAS: Record<string, string[]> = {
  '/owner': ['approvals', 'messages', 'notices', 'results', 'support'], '/desk': ['messages', 'my-pay', 'notices', 'support'],
  '/bursar': ['approvals', 'messages', 'my-pay', 'notices', 'support'], '/teacher': ['messages', 'my-pay'],
  '/parent': ['messages', 'notices', 'results'], '/student': ['results'],
};
const dayLabel = (iso: string) => {
  const d = new Date(iso); const t = new Date(); const y = new Date(); y.setDate(t.getDate() - 1);
  if (d.toDateString() === t.toDateString()) return 'Today';
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
};

/** In-app notifications for any signed-in role, grouped by day; tap one to open what it is about. */
export function NotificationsCenter() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const base = useBase();
  const [page, setPage] = useState(1);
  const [unread, setUnread] = useState(false);
  const q = useQuery({
    queryKey: ['notifications', page, unread],
    queryFn: () => getNotifications({ page, ...(unread ? { unread: 1 } : {}) }).then((r) => ({ rows: unwrap<Row[]>(r) ?? [], meta: metaOf(r) })),
  });
  const refresh = () => { ['notifications', 'notifications-unread', 'nav-unread'].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); };
  const read = useMutation({ mutationFn: (id: number) => postNotificationsRead(id), onSuccess: refresh });
  const readAll = useMutation({ mutationFn: () => postNotificationsReadAll(), onSuccess: refresh });
  const rows = q.data?.rows ?? [];
  const meta: any = q.data?.meta;
  const groups = useMemo(() => {
    const m = new Map<string, Row[]>();
    rows.forEach((n) => { const k = dayLabel(n.at); if (!m.has(k)) m.set(k, []); m.get(k)!.push(n); });
    return [...m.entries()];
  }, [rows]);
  const unreadHere = rows.filter((n) => !n.read).length;

  const openIt = (n: Row) => {
    if (!n.read) read.mutate(n.id);
    const k = kind(n.type);
    if (k.to && HAS[base]?.includes(k.to)) nav(`${base}/${k.to}`);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Me</p><h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Notifications</h1>
          <p className="text-sm text-slate-500">Everything the school and the app told you. Tap one to open it.</p></div>
        <button disabled={readAll.isPending || !unreadHere} onClick={() => readAll.mutate()} className="shrink-0 px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-40"><CheckCheck className="w-4 h-4" />Mark all read</button>
      </div>
      <div className="flex gap-1 bg-slate-100 rounded-2xl p-1 w-fit">
        {([[false, 'All'], [true, 'Unread']] as const).map(([v, l]) => <button key={l} onClick={() => { setUnread(v); setPage(1); }} className={`px-4 py-2 rounded-xl text-sm font-semibold ${unread === v ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}>{l}</button>)}
      </div>

      {q.isLoading ? <p className="text-sm text-slate-500">Loading…</p> : q.isError ? <p className="text-sm text-red-600">Could not load notifications. Pull to refresh.</p> : !rows.length ? (
        <div className="bg-white rounded-3xl border border-slate-200 p-10 text-center">
          <span className="w-14 h-14 rounded-2xl bg-green-50 text-green-600 grid place-items-center mx-auto"><CheckCheck className="w-7 h-7" /></span>
          <p className="font-bold text-slate-900 mt-3">{unread ? 'No unread notifications' : "You're all caught up"}</p>
          <p className="text-sm text-slate-500 mt-1">Payments, approvals, results, notices and messages will show up here.</p>
        </div>
      ) : groups.map(([day, list]) => (
        <section key={day}>
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 px-1">{day}</h2>
          <ul className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
            {list.map((n) => {
              const k = kind(n.type);
              return (
                <li key={n.id}>
                  <button onClick={() => openIt(n)} className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50 ${n.read ? '' : 'bg-blue-50/40'}`}>
                    <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${k.tone}`}><k.icon className="w-5 h-5" /></span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-2"><span className={`text-sm truncate ${n.read ? 'text-slate-700' : 'font-bold text-slate-900'}`}>{n.title}</span>{!n.read && <span className="w-2 h-2 rounded-full bg-blue-600 shrink-0" />}</span>
                      {n.body && <span className="block text-sm text-slate-500 line-clamp-2 whitespace-pre-wrap">{n.body}</span>}
                      <span className="block text-[11px] text-slate-400 mt-0.5">{new Date(n.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                    </span>
                    {k.to && HAS[base]?.includes(k.to) && <ChevronRight className="w-4 h-4 text-slate-300 mt-3 shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      {meta && meta.last_page > 1 && (
        <div className="flex items-center justify-between text-sm">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-2 rounded-xl border border-slate-300 font-semibold disabled:opacity-40">Newer</button>
          <span className="text-slate-500">Page {page} of {meta.last_page}</span>
          <button disabled={page >= meta.last_page} onClick={() => setPage(page + 1)} className="px-3 py-2 rounded-xl border border-slate-300 font-semibold disabled:opacity-40">Older</button>
        </div>
      )}
    </div>
  );
}
